import { api } from "~/core/api/client";
import type {
  ChatAssignee,
  ChatMessage,
  ChatMessagePage,
  ChatRoom,
  ChatRoomStatus,
  ChatRoomType,
  ChatUnreadCount,
} from "~/types/chat";
import type { TransactionPage } from "~/types/transaction";

/** Rooms per page of the inbox ("Load more" fetches the next one). */
export const ROOM_PAGE_SIZE = 50;
/** Messages per page of a transcript ("Load earlier messages" fetches the next one). */
export const MESSAGE_PAGE_SIZE = 50;

export interface RoomFilters {
  status?: ChatRoomStatus | "";
  room_type?: ChatRoomType | "";
  assigned_admin_id?: string;
  /** Searches customer / guest name, phone and subject. */
  q?: string;
  /** 1-based. */
  page?: number;
  limit?: number;
}

export const listRooms = (filters: RoomFilters) =>
  api.get<ChatRoom[]>("/admin/chat/rooms", {
    page: filters.page ?? 1,
    limit: filters.limit ?? ROOM_PAGE_SIZE,
    status: filters.status || undefined,
    room_type: filters.room_type || undefined,
    assigned_admin_id: filters.assigned_admin_id || undefined,
    q: filters.q?.trim() || undefined,
  });

export const getRoom = (id: string) => api.get<ChatRoom>(`/admin/chat/rooms/${id}`);

/** Newest first. `cursor` is the id of the oldest message already loaded. */
export const listMessages = (id: string, cursor?: string | null, limit = MESSAGE_PAGE_SIZE) =>
  api.get<ChatMessagePage>(`/admin/chat/rooms/${id}/messages`, {
    limit,
    cursor: cursor || undefined,
  });

export const sendMessage = (id: string, content: string) =>
  api.post<ChatMessage>(`/admin/chat/rooms/${id}/messages`, { content, message_type: "text" });

/** A message carrying an uploaded file. `content` must be non-empty (caption or filename). */
export const sendAttachment = (
  id: string,
  body: { content: string; message_type: "image" | "file"; attachment_url: string },
) => api.post<ChatMessage>(`/admin/chat/rooms/${id}/messages`, body);

export const assignRoom = (id: string, admin_id: string) =>
  api.patch<ChatRoom>(`/admin/chat/rooms/${id}/assign`, { admin_id });

export const unassignRoom = (id: string) =>
  api.patch<ChatRoom>(`/admin/chat/rooms/${id}/unassign`);

/** Staff a conversation can be handed to. */
export const listAssignees = () => api.get<ChatAssignee[]>("/admin/chat/assignees");

export const getUnreadCount = () => api.get<ChatUnreadCount>("/admin/chat/unread-count");

export const setRoomStatus = (id: string, status: ChatRoomStatus) =>
  api.patch<ChatRoom>(`/admin/chat/rooms/${id}/status`, { status });

export const markRoomRead = (id: string) =>
  api.patch<void>(`/admin/chat/rooms/${id}/messages/read`);

/**
 * Filters for the customer's open orders shown beside a conversation. Exported so
 * the query key is built from the exact object the request uses.
 */
export const customerOpenOrdersFilters = (userId: string) =>
  ({ user_id: userId, main_status: "Ongoing", limit: 10 }) as const;

/** A customer's ongoing orders (`main_status` is PascalCase on the wire). */
export const listCustomerOpenOrders = (userId: string) =>
  api.get<TransactionPage>("/admin/transactions", { ...customerOpenOrdersFilters(userId) });
